import { translateUi } from "@/shared/lib/ui-translate";
import type { DomainBracket } from "../model/domain";
import type { ApiBracketDTO } from "../api/types";
import type { BracketData, BracketMatch } from "../model/types";

/**
 * Определяет тип брекета для Double Elimination на основе position из бэкенда
 * Backend convention:
 * - Winners Bracket: position < 1000
 * - Losers Bracket: position >= 1000 && position < 9999
 * - Grand Finals: position === 9999
 */
function determineBracketType(
  format: string,
  position?: number,
): "winners" | "losers" | "finals" | undefined {
  if (format !== "double_elimination") {
    return undefined;
  }

  if (position === 9999) return "finals";
  if (position !== undefined && position >= 1000 && position < 9999)
    return "losers";
  if (position !== undefined && position < 1000) return "winners";

  return undefined;
}

function normalizeBracket(
  raw: unknown,
  format: string,
  position?: number,
): "winners" | "losers" | "finals" | undefined {
  if (format !== "double_elimination") return undefined;
  if (raw === null || raw === undefined) {
    return determineBracketType(format, position);
  }
  if (typeof raw === "number") {
    if (raw === 0) return "winners";
    if (raw === 1) return "losers";
    if (raw === 2) return "finals";
  }
  const text = String(raw).toLowerCase();
  if (
    text.includes("winner") ||
    text.includes("upper") ||
    text.includes("wb")
  ) {
    return "winners";
  }
  if (
    text.includes("loser") ||
    text.includes("lower") ||
    text.includes("lb")
  ) {
    return "losers";
  }
  if (text.includes("final")) {
    return "finals";
  }
  return determineBracketType(format, position);
}


// KAN-203: конвертер принимает и доменную модель, и сырой ответ API, поэтому
// читает объединение полей обеих форм (плюс legacy-ключи team1/team2/sides).
type ConvertibleTeam = {
  id?: string;
  name?: string;
  avatarUrl?: string;
  logo?: string;
  seed?: number;
};

type ConvertibleMatch = {
  id?: string;
  name?: string;
  matchNumber?: number;
  round?: number;
  roundIndex?: number;
  position?: number;
  bracket?: string;
  bestOf?: 1 | 3 | 5;
  phase?: string;
  status?: string;
  scoreA?: number;
  scoreB?: number;
  team1Score?: number;
  team2Score?: number;
  teamAId?: string;
  teamBId?: string;
  team1?: ConvertibleTeam | null;
  team2?: ConvertibleTeam | null;
  sides?: Array<
    | { teamId?: string; score?: number; team?: ConvertibleTeam | null }
    | undefined
  >;
  nextMatchIds?: string[];
  tournamentCode?: string;
  pausedByTeamId?: string | null;
  isTechnicalWin?: boolean;
  pausesAllowedPerMatch?: number;
  team1PausesUsed?: number;
  team2PausesUsed?: number;
  team1PausesRemaining?: number;
  team2PausesRemaining?: number;
  logo?: string;
  team?: unknown;
};

/** Источник конвертации: доменная модель либо сырой DTO сетки. */
type ConvertibleBracket = (DomainBracket | ApiBracketDTO) & {
  type?: string;
  seedOrder?: string[] | null;
  bracketId?: string;
};

function isBracketFormat(value: string): value is BracketData["format"] {
  return (
    value === "single_elimination" ||
    value === "double_elimination" ||
    value === "round_robin" ||
    value === "swiss"
  );
}

export function convertDomainToBracketData(
  domain: DomainBracket | ApiBracketDTO,
): BracketData {
  // Нормализация формата
  const src = domain as ConvertibleBracket;
  const rawFormat = src.type ?? src.format;
  const format =
    rawFormat === "SingleElimination"
      ? "single_elimination"
      : rawFormat === "DoubleElimination"
        ? "double_elimination"
        : rawFormat === "RoundRobin"
          ? "round_robin"
          : rawFormat === "SwissSystem"
            ? "swiss"
            : rawFormat;
  if (typeof format !== "string" || !isBracketFormat(format)) {
    throw new Error(`Unsupported bracket format: ${String(rawFormat)}`);
  }

  // Извлечение команд из матчей (для нового API)
  const teamsMap = new Map<
    string,
    { id: string; name: string; avatar?: string }
  >();
  const matchIds = new Set<string>();

  const matches: BracketMatch[] = (domain.matches as ConvertibleMatch[]).map(
    (m) => {
    const matchId = typeof m.id === "string" ? m.id.trim() : "";
    if (!matchId) {
      throw new Error("Bracket match is missing a valid id");
    }
    if (matchIds.has(matchId)) {
      throw new Error(`Duplicate bracket match id: ${matchId}`);
    }
    matchIds.add(matchId);
    const bracket = normalizeBracket(m.bracket, format, m.position);
    const sideA = Array.isArray(m.sides) ? m.sides[0] : undefined;
    const sideB = Array.isArray(m.sides) ? m.sides[1] : undefined;

    // Извлекаем команды
    if (m.team1?.id && m.team1.id !== null) {
      teamsMap.set(m.team1.id, {
        id: m.team1.id,
        name: m.team1.name ?? translateUi("litDomainConvert.neizvestnayaKomanda"),
        ...(m.team1.logo ? { avatar: m.team1.logo } : {}),
      });
    }
    if (m.team2?.id && m.team2.id !== null) {
      teamsMap.set(m.team2.id, {
        id: m.team2.id,
        name: m.team2.name ?? translateUi("litDomainConvert.neizvestnayaKomanda"),
        ...(m.team2.logo ? { avatar: m.team2.logo } : {}),
      });
    }
    if (sideA?.team?.id) {
      teamsMap.set(sideA.team.id, {
        id: sideA.team.id,
        name: sideA.team.name ?? translateUi("litDomainConvert.neizvestnayaKomanda"),
        ...(sideA.team.logo ? { avatar: sideA.team.logo } : {}),
      });
    }
    if (sideB?.team?.id) {
      teamsMap.set(sideB.team.id, {
        id: sideB.team.id,
        name: sideB.team.name ?? translateUi("litDomainConvert.neizvestnayaKomanda"),
        ...(sideB.team.logo ? { avatar: sideB.team.logo } : {}),
      });
    }

    return {
      id: matchId,
      ...(m.name !== undefined ? { name: m.name } : {}),
      ...(m.round !== undefined
        ? { roundIndex: m.round }
        : m.roundIndex !== undefined
          ? { roundIndex: m.roundIndex }
          : {}),
      ...(bracket !== undefined ? { bracket } : {}),
      ...(m.bestOf !== undefined ? { bestOf: m.bestOf } : {}),
      ...("matchNumber" in m && m.matchNumber !== undefined
        ? { matchNumber: m.matchNumber }
        : {}),
      sides: [
        {
          ...(m.team1?.id !== undefined && m.team1.id !== null
            ? { teamId: m.team1.id }
            : m.teamAId !== undefined
              ? { teamId: m.teamAId }
              : sideA?.teamId !== undefined
                ? { teamId: sideA.teamId }
                : sideA?.team?.id !== undefined
                  ? { teamId: sideA.team.id }
                  : {}),
          ...(m.team1Score !== undefined
            ? { score: m.team1Score }
            : m.scoreA !== undefined
              ? { score: m.scoreA }
              : sideA?.score !== undefined
                ? { score: sideA.score }
                : {}),
        },
        {
          ...(m.team2?.id !== undefined && m.team2.id !== null
            ? { teamId: m.team2.id }
            : m.teamBId !== undefined
              ? { teamId: m.teamBId }
              : sideB?.teamId !== undefined
                ? { teamId: sideB.teamId }
                : sideB?.team?.id !== undefined
                  ? { teamId: sideB.team.id }
                  : {}),
          ...(m.team2Score !== undefined
            ? { score: m.team2Score }
            : m.scoreB !== undefined
              ? { score: m.scoreB }
              : sideB?.score !== undefined
                ? { score: sideB.score }
                : {}),
        },
      ],
      ...(m.nextMatchIds !== undefined && m.nextMatchIds.length > 0
        ? { nextMatchIds: m.nextMatchIds }
        : {}),
      ...("tournamentCode" in m && m.tournamentCode !== undefined
        ? { tournamentCode: m.tournamentCode }
        : {}),
      ...(m.status !== undefined ? { status: m.status } : {}),
      ...("phase" in m && m.phase !== undefined ? { phase: m.phase } : {}),
      ...(m.pausesAllowedPerMatch !== undefined ? { pausesAllowedPerMatch: m.pausesAllowedPerMatch } : {}),
      ...(m.team1PausesUsed !== undefined ? { team1PausesUsed: m.team1PausesUsed } : {}),
      ...(m.team2PausesUsed !== undefined ? { team2PausesUsed: m.team2PausesUsed } : {}),
      ...(m.team1PausesRemaining !== undefined ? { team1PausesRemaining: m.team1PausesRemaining } : {}),
      ...(m.team2PausesRemaining !== undefined ? { team2PausesRemaining: m.team2PausesRemaining } : {}),
      ...(m.pausedByTeamId !== undefined && m.pausedByTeamId !== null ? { pausedByTeamId: m.pausedByTeamId } : {}),
      ...(m.isTechnicalWin ? { isTechnicalWin: true } : {}),
    };
  });

  // Используем команды из domain.teams если есть, иначе из извлечённых
  let teams = [];
  // DomainTeam и ApiTeamDTO различаются полями аватара — читаем оба варианта.
  const rawTeams = src.teams as ConvertibleTeam[] | undefined;
  if (rawTeams && Array.isArray(rawTeams)) {
    const teamIds = new Set<string>();
    teams = rawTeams.map((t) => {
      const teamId = typeof t.id === "string" ? t.id.trim() : "";
      if (!teamId) {
        throw new Error("Bracket team is missing a valid id");
      }
      if (teamIds.has(teamId)) {
        throw new Error(`Duplicate bracket team id: ${teamId}`);
      }
      teamIds.add(teamId);
      const base = {
        id: teamId,
        name: t.name ?? translateUi("litDomainConvert.neizvestnayaKomanda"),
      };
      const withAvatar =
        t.avatarUrl !== undefined
          ? { ...base, avatar: t.avatarUrl }
          : t.logo !== undefined
            ? { ...base, avatar: t.logo }
            : base;
      return t.seed !== undefined
        ? { ...withAvatar, seed: t.seed }
        : withAvatar;
    });
  } else {
    teams = Array.from(teamsMap.values());
  }

  const seedOrder = src.seedOrder;
  return {
    id: src.bracketId ?? src.id,
    format,
    teams,
    matches,
    ...(Array.isArray(seedOrder) ? { seedOrder } : {}),
  };
}
